import { useState } from 'react';
import { View, Text, ScrollView, StyleSheet, Linking } from 'react-native';
import { router, type Href } from 'expo-router';
import Head from 'expo-router/head';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CalendarX, CheckCircle2, Download, ExternalLink, Link2Off } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  TYPOGRAPHY,
  BORDER_RADIUS,
  READING_MAX_WIDTH,
  withWeight,
} from '../../theme';
import { Button, EmptyState, LoadingSpinner, RediLogo, TextLink } from '@components/common';
import { EventSignupForm } from './EventSignupForm';
import type { PublicPlanEvent } from '../../hooks/usePublicPlanEvent';
import {
  buildIcs,
  directionsUrl,
  eventDescription,
  eventIcsFilename,
  formatEventWhen,
  googleCalendarUrl,
  isEventUpcoming,
} from '../../utils/eventCalendar';
import { isDownloadSupported, triggerDownload } from '../../utils/downloadFile';
import { planEventShareUrl } from '../../utils/share';
import type { PlanEvent } from '../../types';

/** How many defenses the page lists before it says "and N more". */
const WORK_LIST_LIMIT = 8;

function EventPageHeader() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  return (
    <View
      style={[
        styles.header,
        { paddingTop: insets.top + SPACING.md, borderBottomColor: colors.border },
      ]}
      testID="event-header">
      <View style={styles.headerInner}>
        <RediLogo fontSize={20} />
      </View>
    </View>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <View style={[styles.container, { backgroundColor: colors.background }]} testID="event-page">
      <Head>
        <title>{title}</title>
      </Head>
      <EventPageHeader />
      {children}
    </View>
  );
}

// Native has no browser download, so there the .ics button is hidden and
// Google Calendar is the one way in; the rest of the page reads the same.
function CalendarButtons({ event }: { event: PlanEvent }) {
  const url = planEventShareUrl(event.id);
  const description = eventDescription(event, url);
  const handleIcs = () => {
    const ics = buildIcs(event, { url, description });
    // No BOM: a byte-order mark ahead of BEGIN:VCALENDAR trips some calendar apps.
    triggerDownload(
      new Blob([ics], { type: 'text/calendar;charset=utf-8' }),
      eventIcsFilename(event)
    );
  };
  return (
    <View style={styles.buttonRow}>
      {isDownloadSupported() && (
        <Button
          title="Add to calendar"
          onPress={handleIcs}
          variant="secondary"
          size="sm"
          leftIcon={Download}
          style={styles.half}
          testID="event-ics"
        />
      )}
      <Button
        title="Google Calendar"
        onPress={() => void Linking.openURL(googleCalendarUrl(event, description))}
        variant="secondary"
        size="sm"
        rightIcon={ExternalLink}
        style={styles.half}
        testID="event-gcal"
      />
    </View>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <View style={styles.detail}>
      <Text style={[styles.overline, { color: colors.textSecondary }]}>{label}</Text>
      {children}
    </View>
  );
}

/**
 * /event/<id>: the public page for one plan event. Anyone with the link sees
 * the time and place and can sign up with a name and an email, no account. It
 * works signed out, so the root layout lists it with the other public routes.
 */
export function EventPageView({
  state,
  noteGoingCount,
}: {
  state: PublicPlanEvent;
  noteGoingCount: (goingCount: number) => void;
}) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const [signedUp, setSignedUp] = useState<{ updated: boolean } | null>(null);
  const now = new Date();

  if (state.status === 'loading') {
    return (
      <Shell title="Event - Redi">
        <View style={styles.centered}>
          <LoadingSpinner />
        </View>
      </Shell>
    );
  }

  if (state.status === 'missing' || state.status === 'error') {
    const missing = state.status === 'missing';
    return (
      <Shell title="Event - Redi">
        <View style={styles.centered} testID={missing ? 'event-missing' : 'event-error'}>
          <EmptyState
            icon={missing ? Link2Off : CalendarX}
            title={missing ? "We couldn't find this event." : 'Something went wrong.'}
            message={
              missing
                ? 'Check that you copied the whole link, or ask the organizer to send it again.'
                : 'Try again in a moment.'
            }
          />
          <View style={styles.links}>
            <TextLink label="See what REDI is" onPress={() => router.push('/feed')} />
          </View>
        </View>
      </Shell>
    );
  }

  const { event, work, leaderName, readablePlanId } = state;
  const cancelled = event.status === 'cancelled';
  const upcoming = isEventUpcoming(event, now);
  const ended = !cancelled && !upcoming;
  const going = event.goingCount;

  return (
    <Shell title={`${event.title} - Redi`}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: insets.bottom + SPACING.xl },
        ]}>
        <View style={styles.column}>
          <Text style={[styles.title, { color: colors.text }]} testID="event-title">
            {event.title}
          </Text>

          {cancelled && (
            <View
              style={[styles.banner, { backgroundColor: colors.dangerSoft }]}
              testID="event-cancelled">
              <Text style={[styles.bannerText, { color: colors.danger }]}>
                This event was cancelled.
              </Text>
            </View>
          )}
          {ended && (
            <View
              style={[
                styles.banner,
                { backgroundColor: colors.surface, borderColor: colors.border },
              ]}
              testID="event-ended">
              <Text style={[styles.bannerText, { color: colors.textSecondary }]}>
                This event has ended.
              </Text>
            </View>
          )}

          <Detail label="When">
            <Text style={[styles.value, { color: colors.text }]} testID="event-when">
              {formatEventWhen(event)}
            </Text>
          </Detail>

          <Detail label="Meet at">
            <Text style={[styles.value, { color: colors.text }]} testID="event-place">
              {event.meetingPlace.label}
            </Text>
            <View style={styles.inlineAction}>
              <Button
                title="Directions"
                onPress={() => void Linking.openURL(directionsUrl(event.meetingPlace))}
                variant="secondary"
                size="sm"
                rightIcon={ExternalLink}
                testID="event-directions"
              />
            </View>
          </Detail>

          {!!event.whatToBring && (
            <Detail label="What to bring">
              <Text style={[styles.value, { color: colors.text }]} testID="event-bring">
                {event.whatToBring}
              </Text>
            </Detail>
          )}

          {!!event.notes && (
            <Detail label="Notes">
              <Text style={[styles.value, { color: colors.text }]} testID="event-notes">
                {event.notes}
              </Text>
            </Detail>
          )}

          {work && work.length > 0 && (
            <Detail label="The work">
              <View testID="event-work">
                {work.slice(0, WORK_LIST_LIMIT).map((label, i) => (
                  <Text key={`${label}-${i}`} style={[styles.value, { color: colors.text }]}>
                    {label}
                  </Text>
                ))}
                {work.length > WORK_LIST_LIMIT && (
                  <Text style={[styles.value, { color: colors.textSecondary }]}>
                    {`and ${work.length - WORK_LIST_LIMIT} more`}
                  </Text>
                )}
              </View>
              {leaderName ? (
                <Text style={[styles.meta, { color: colors.textSecondary }]} testID="event-leader">
                  {`Led by ${leaderName}`}
                </Text>
              ) : null}
            </Detail>
          )}

          <Text style={[styles.going, { color: colors.textSecondary }]} testID="event-going">
            {going > 0 ? `${going} going` : 'No one has signed up yet'}
          </Text>

          {upcoming && (
            <View style={styles.calendar}>
              <CalendarButtons event={event} />
            </View>
          )}

          {readablePlanId && (
            <View style={styles.links}>
              <TextLink
                label="See the whole plan"
                onPress={() => router.push(`/plan/${readablePlanId}` as Href)}
                testID="event-plan-link"
              />
            </View>
          )}

          {upcoming && (
            <View
              style={[styles.signupCard, { borderColor: colors.border }]}
              testID="event-signup-card">
              {signedUp ? (
                <View style={styles.success} testID="event-signup-success">
                  <CheckCircle2 size={28} color={colors.action} />
                  <Text style={[styles.successTitle, { color: colors.text }]}>
                    {signedUp.updated
                      ? 'We updated your sign-up.'
                      : "You're signed up. See you there."}
                  </Text>
                  <CalendarButtons event={event} />
                </View>
              ) : (
                <>
                  <Text style={[styles.signupTitle, { color: colors.text }]}>Sign up</Text>
                  <EventSignupForm
                    eventId={event.id}
                    onSignedUp={(result) => {
                      noteGoingCount(result.goingCount);
                      setSignedUp({ updated: result.updated });
                    }}
                  />
                </>
              )}
            </View>
          )}
        </View>
      </ScrollView>
    </Shell>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    paddingBottom: SPACING.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerInner: {
    width: '100%',
    maxWidth: READING_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.lg,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    alignItems: 'center',
    paddingTop: SPACING.xl,
  },
  column: {
    width: '100%',
    maxWidth: READING_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
  },
  title: {
    ...TYPOGRAPHY.screenTitle,
    marginBottom: SPACING.md,
  },
  banner: {
    borderRadius: BORDER_RADIUS.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'transparent',
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.md,
    marginBottom: SPACING.md,
  },
  bannerText: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  detail: {
    marginTop: SPACING.lg,
  },
  overline: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.xs,
  },
  value: {
    ...TYPOGRAPHY.body,
  },
  meta: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.xs,
  },
  inlineAction: {
    alignSelf: 'flex-start',
    marginTop: SPACING.sm,
  },
  going: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
    marginTop: SPACING.lg,
  },
  calendar: {
    marginTop: SPACING.md,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: SPACING.sm,
  },
  half: {
    flex: 1,
  },
  links: {
    marginTop: SPACING.md,
    alignItems: 'flex-start',
    gap: SPACING.sm,
  },
  signupCard: {
    marginTop: SPACING.xl,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.lg,
    padding: SPACING.lg,
  },
  signupTitle: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.md,
  },
  success: {
    alignItems: 'center',
    gap: SPACING.md,
  },
  successTitle: {
    ...TYPOGRAPHY.h4,
    textAlign: 'center',
  },
});
